import { call, put, select, takeLatest } from 'redux-saga/effects';

import { monolithApiUrl } from '../../config/app';
import { endpoints } from '../../constants/routing';
import { IReduxAction } from '../../types/redux';
import { IAddUserActionPayload, IDeleteUserActionPayload, IEditUserActionPayload } from '../../types/users';
import { buildEndpointRoute } from '../../utils/routing';
import * as UsersActions from '../actions/users';
import { selectCurrentOrganizationId } from '../selectors/organizations';
import { AuthRequest } from './helpers';

export function* usersWorkerSaga(): Generator<any, any, any> {
  try {
    const endpoint = `${monolithApiUrl}${endpoints.users}`;

    const response = yield call<any>(AuthRequest, { endpoint });

    if (!response.ok) {
      yield put(UsersActions.fetchUsersFailure(response.body));
    } else {
      yield put(UsersActions.fetchUsersSuccess(response.body));
    }
  } catch (error) {
    yield put(UsersActions.fetchUsersFailure(error));
  }
}

export function* addUserWorkerSaga(action: IReduxAction<IAddUserActionPayload>): Generator<any, any, any> {
  const { user, resolve, reject } = action.payload!;

  // tslint:disable-next-line:variable-name
  const org_id = yield select(selectCurrentOrganizationId);

  try {
    const endpoint = `${monolithApiUrl}${endpoints.addUser}`;

    const response = yield call(AuthRequest, { endpoint, method: 'POST', body: JSON.stringify({ ...user, org_id }) });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(UsersActions.addUserFailure(response.body));
    } else {
      yield put(UsersActions.addUserSuccess({ ...user, ...response.body }));

      if (typeof resolve === 'function') {
        resolve();
      }

      // TODO: Remove the following line after the endpoint returns UID of our newly created user
      yield put(UsersActions.fetchUsersRequest());
    }
  } catch (e) {
    if (typeof reject === 'function') {
      reject();
    }
    yield put(UsersActions.addUserFailure(e));
  }
}

export function* editUserWorkerSaga(action: IReduxAction<IEditUserActionPayload>): Generator<any, any, any> {
  const { user, resolve, reject } = action.payload!;

  try {
    if (!user.uid) {
      throw new Error('User ID is required');
    }

    const endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.editUser, { id: user.uid })}`;

    const response = yield call(AuthRequest, { endpoint, method: 'PATCH', body: JSON.stringify({ ...user }) });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(UsersActions.editUserFailure(response.body));
    } else {
      yield put(UsersActions.editUserSuccess(user));
      if (typeof resolve === 'function') {
        resolve();
      }
    }
  } catch (e) {
    if (typeof reject === 'function') {
      reject(e);
    }
    yield put(UsersActions.editUserFailure(e));
  }
}

function* deleteUserWorkerSaga(action: IReduxAction<IDeleteUserActionPayload>): Generator<any, any, any> {
  const { userId, newOwnerId, resolve, reject } = action.payload!;
  try {
    let endpoint = `${monolithApiUrl}${buildEndpointRoute(endpoints.deleteUser, {
      id: userId,
    })}`;

    if (newOwnerId) {
      endpoint += `?new_owner_id=${newOwnerId}`;
    }

    const response = yield call(AuthRequest, { endpoint, method: 'DELETE' });

    if (!response.ok) {
      if (typeof reject === 'function') {
        reject(response.body);
      }
      yield put(UsersActions.deleteUserFailure(response.body));
    } else {
      yield put(UsersActions.deleteUserSuccess({ userId }));
      if (typeof resolve === 'function') {
        resolve();
      }
    }
  } catch (e) {
    if (typeof reject === 'function') {
      reject(e);
    }
    yield put(UsersActions.editUserFailure(e));
  }
}

const sagas = {
  *watchUsersRequest() {
    yield takeLatest(UsersActions.FETCH_USERS_REQUEST, usersWorkerSaga);
  },
  *watchAddUserRequest() {
    yield takeLatest(UsersActions.ADD_USER_REQUEST, addUserWorkerSaga);
  },
  *watchEditUserRequest() {
    yield takeLatest(UsersActions.EDIT_USER_REQUEST, editUserWorkerSaga);
  },
  *watchDeleteUserRequest() {
    yield takeLatest(UsersActions.DELETE_USER_REQUEST, deleteUserWorkerSaga);
  },
};

export default sagas;
